/**
 * Public landing page — replaces the previous "embed a login card in the
 * hero" approach. The Login button opens `LoginModal` instead, which calls
 * the exact same `useAuth().login()` the standalone `/login` page uses; no
 * authentication logic lives on this page.
 *
 * Rendered by `HomeRoute` at "/" for unauthenticated visitors only —
 * already-authenticated users are redirected straight to `/dashboard`.
 */

import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import LoginModal from "@/pages/landing/LoginModal";
import HeroSection from "@/pages/landing/HeroSection";
import BrokerIntegrationsSection from "@/pages/landing/BrokerIntegrationsSection";
import FeatureCardsSection from "@/pages/landing/FeatureCardsSection";
import HowItWorksSection from "@/pages/landing/HowItWorksSection";
import PlatformPreviewSection from "@/pages/landing/PlatformPreviewSection";
import PricingSection from "@/pages/landing/PricingSection";
import TestimonialsSection from "@/pages/landing/TestimonialsSection";
import AboutSection from "@/pages/landing/AboutSection";
import FaqSection from "@/pages/landing/FaqSection";
import ContactSection from "@/pages/landing/ContactSection";
import LandingFooter from "@/pages/landing/LandingFooter";

const NAV_LINKS = [
  { label: "Features", href: "#features" },
  { label: "Solutions", href: "#solutions" },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

function LandingLogo({ onClick }: { onClick?: () => void } = {}) {
  return (
    <Stack
      component={RouterLink}
      to="/"
      onClick={onClick}
      direction="row"
      alignItems="center"
      spacing={1}
      aria-label="TekStar Invest — go to home page"
      sx={{
        textDecoration: "none",
        cursor: "pointer",
        borderRadius: 1,
        transition: "opacity .15s ease",
        "&:hover": { opacity: 0.8 },
        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <ShowChartIcon sx={{ color: "#fff", fontSize: 18 }} />
      </Box>
      <Typography variant="h6" fontWeight={800} color="primary.main">
        TekStar Invest
      </Typography>
    </Stack>
  );
}

function LandingNav({ onLoginClick }: { onLoginClick: () => void }) {
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 4 });
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={scrolled ? 2 : 0}
        sx={{
          bgcolor: "#fff",
          color: "text.primary",
          borderBottom: scrolled ? "none" : "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LandingLogo />
            </Box>

            <Stack direction="row" spacing={3.5} alignItems="center" sx={{ display: { xs: "none", md: "flex" }, mr: 3 }}>
              {NAV_LINKS.map((link) => (
                <Typography
                  key={link.label}
                  component="a"
                  href={link.href}
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "text.secondary", textDecoration: "none", "&:hover": { color: "primary.main" } }}
                >
                  {link.label}
                </Typography>
              ))}
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ display: { xs: "none", sm: "flex" } }}>
              <Button onClick={onLoginClick}>Login</Button>
              <Button component={RouterLink} to="/register" variant="contained">
                Create Free Account
              </Button>
            </Stack>

            <IconButton
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: "inline-flex", sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={mobileOpen} onClose={closeMobile}>
        <Box sx={{ width: 280, p: 3 }} role="presentation">
          <Box mb={3}>
            <LandingLogo onClick={closeMobile} />
          </Box>
          <Stack spacing={2} mb={3}>
            {NAV_LINKS.map((link) => (
              <Typography
                key={link.label}
                component="a"
                href={link.href}
                variant="body1"
                fontWeight={600}
                onClick={closeMobile}
                sx={{ color: "text.primary", textDecoration: "none" }}
              >
                {link.label}
              </Typography>
            ))}
          </Stack>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={1.5}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                closeMobile();
                onLoginClick();
              }}
            >
              Login
            </Button>
            <Button component={RouterLink} to="/register" variant="contained" fullWidth onClick={closeMobile}>
              Create Free Account
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

export function LandingPage() {
  const [loginOpen, setLoginOpen] = useState(false);

  return (
    <Box>
      <LandingNav onLoginClick={() => setLoginOpen(true)} />
      <LoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />

      <HeroSection />
      <BrokerIntegrationsSection />
      <FeatureCardsSection />
      <HowItWorksSection />
      <PlatformPreviewSection />
      <PricingSection />
      <TestimonialsSection />
      <AboutSection />
      <FaqSection />
      <ContactSection />
      <LandingFooter />
    </Box>
  );
}

export default LandingPage;
